import { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  :root {
    /* Cores principais */
    --color-primary: #000000;
    --color-secondary: #111111;
    --color-accent: #d4af37;
    --color-accent-light: #e6c457;
    --color-accent-dark: #b3941e;
    
    /* Cores de texto */
    --color-text: #ffffff;
    --color-text-secondary: #cccccc;
    --color-text-tertiary: #999999;
    
    /* Cores de fundo */
    --color-background: #000000;
    --color-background-light: #1a1a1a;
    --color-background-dark: #0a0a0a;
    
    /* Gradientes */
    --gradient-accent: linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-light) 100%);
    --gradient-dark: linear-gradient(135deg, #111111 0%, #222222 100%);
    --gradient-glass: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
    
    /* Fontes */
    --font-primary: 'Playfair Display', serif;
    --font-secondary: 'Poppins', sans-serif;
    
    /* Tamanhos */
    --container-width: 1200px;
    --header-height: 80px;
    --border-radius: 4px;
    --border-radius-lg: 12px;
    
    /* Sombras */
    --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.15);
    --shadow-md: 0 5px 15px rgba(0, 0, 0, 0.2);
    --shadow-lg: 0 10px 30px rgba(0, 0, 0, 0.3);
    --shadow-accent: 0 5px 15px rgba(212, 175, 55, 0.2);
    --shadow-accent-lg: 0 10px 25px rgba(212, 175, 55, 0.25);
    --shadow-inset: inset 0 1px 3px rgba(0, 0, 0, 0.1);
    
    /* Transições */
    --transition-fast: 0.2s ease;
    --transition-normal: 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    --transition-slow: 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    
    /* Efeitos */
    --blur-sm: blur(4px);
    --blur-md: blur(8px);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    font-size: 16px;
    
    @media (max-width: 1200px) {
      font-size: 15px;
    }
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
    
    /* Barra de rolagem personalizada */
    scrollbar-width: thin;
    scrollbar-color: var(--color-accent) var(--color-background-light);
    
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: var(--color-background-light);
    }
    
    &::-webkit-scrollbar-thumb {
      background-color: var(--color-accent);
      border-radius: 4px;
    }
  }

  body {
    font-family: var(--font-secondary);
    background-color: var(--color-background);
    color: var(--color-text);
    overflow-x: hidden;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-primary);
    font-weight: 600;
    margin-bottom: 1rem;
    line-height: 1.3;
  }
  
  h1 {
    font-size: 3.5rem;
  }
  
  h2 {
    font-size: 2.5rem;
    position: relative;
    
    &.section-title {
      text-align: center;
      margin-bottom: 3rem;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -15px;
        left: 50%;
        transform: translateX(-50%);
        width: 80px;
        height: 3px;
        background: var(--gradient-accent);
        border-radius: 2px;
      }
    }
  }
  
  h3 {
    font-size: 1.75rem;
  }
  
  h4 {
    font-size: 1.5rem;
  }

  p {
    line-height: 1.7;
    margin-bottom: 1rem;
    color: var(--color-text-secondary);
  }

  a {
    color: var(--color-accent);
    text-decoration: none;
    transition: var(--transition-normal);

    &:hover {
      color: var(--color-accent-light);
    }
  }

  button {
    cursor: pointer;
    font-family: var(--font-secondary);
    border: none;
    background: none;
    transition: var(--transition-normal);
    
    &:focus {
      outline: none;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  section {
    padding: 6rem 0;
    position: relative;
    
    @media (max-width: 768px) {
      padding: 4rem 0;
    }
  }

  .container {
    width: 100%;
    max-width: var(--container-width);
    margin: 0 auto;
    padding: 0 2rem;
  }
  
  .glassmorphism {
    background: var(--gradient-glass);
    backdrop-filter: var(--blur-sm);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: var(--border-radius-lg);
  }
  
  .highlight-text {
    position: relative;
    display: inline-block;
    color: var(--color-accent);
    z-index: 1;
    
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 10px;
      bottom: 5px;
      left: 0;
      background-color: rgba(212, 175, 55, 0.15);
      z-index: -1;
      transform: skewX(-10deg);
    }
  }
  
  ::selection {
    background-color: var(--color-accent);
    color: var(--color-primary);
  }

  @media (max-width: 992px) {
    .hide-on-tablet {
      display: none !important;
    }
  }
  
  @media (max-width: 768px) {
    section {
      padding: 4rem 0;
    }
    
    .hide-on-mobile {
      display: none !important;
    }
  }
  
  @media (max-width: 480px) {
    .hide-on-small {
      display: none !important;
    }
  }
  
  /* Animações */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  @keyframes shimmer {
    0% { background-position: -100% 0; }
    100% { background-position: 200% 0; }
  }
  
  .shimmer-effect {
    background: linear-gradient(
      90deg,
      var(--color-background) 0%,
      var(--color-accent-light) 50%,
      var(--color-background) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 2.5s infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }
`; 